import _ from 'lodash'
import React from 'react'
import classNames from 'classnames'
import Promise from 'bluebird'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { GeolocationError, getCurrentGeoPosition } from 'browser/app/utils/utils'
import { translateString } from 'shared-libs/helpers/utils'
import {
  GeolocationCoords,
  buildMapsUrl,
  hasCoords,
  resolveDestinationGeolocation,
  resolveDistanceText,
} from 'shared-libs/components/ui2/fields/ui2-geolocation-field/geolocation-distance'
import {
  DEFAULT_OPEN_IN_MAPS_TEXT,
  DEFAULT_TROUBLESHOOT_TEXT,
  GeolocationBanner,
  GeolocationStatus,
  resolveGeolocationBanner,
} from 'shared-libs/components/ui2/fields/ui2-geolocation-field/geolocation-status'
import { resolveTroubleshootingUrl } from 'shared-libs/components/ui2/fields/ui2-geolocation-field/location-troubleshooting'
import type { UI2GeolocationFieldProps } from 'shared-libs/components/ui2/fields/ui2-geolocation-field/ui2-geolocation-field-props'
import { UI2_GEOLOCATION_FIELD_DEFAULT_PROPS } from 'shared-libs/components/ui2/fields/ui2-geolocation-field/ui2-geolocation-field-props'

import { UI2Banner } from 'browser/components/ui2/display/ui2-banner/ui2-banner'
import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { UI2IconNames } from 'shared-libs/components/ui2/icons/ui2-icon-names'
import { UI2FocusTargetRegistration } from '../../behavior/focus-target-registration/focus-target-registration'
import './_ui2-geolocation-field.scss'

interface IUI2GeolocationFieldWebExtras extends Omit<IBaseProps, 'children'> {
  onChange?: (value: any) => void
  transientValue?: string
}

/**
 * @uiComponent
 */
export interface IUI2GeolocationFieldProps
  extends Omit<UI2GeolocationFieldProps, 'onChange'>,
    IUI2GeolocationFieldWebExtras {}

interface IUI2GeolocationFieldState {
  status: GeolocationStatus
  position?: GeolocationCoords
}

let CACHED_LOCATION: GeolocationCoords | undefined

export class UI2GeolocationField extends React.Component<
  IUI2GeolocationFieldProps,
  IUI2GeolocationFieldState
> {
  public static defaultProps: Partial<IUI2GeolocationFieldProps> = {
    ...UI2_GEOLOCATION_FIELD_DEFAULT_PROPS,
    minSecondsBetweenUpdates: 5, // web-specific: mobile default is 1.5 s (shared constant)
  }

  /** See the NM twin — the required gate reads coordinates, not truthiness. */
  public static isValueEmptyForRequired(value: GeolocationCoords | undefined): boolean {
    return !hasCoords(value)
  }

  private request?: Promise<any>
  private permissionStatus?: PermissionStatus
  private watchId: number | null = null
  private throttledWatchHandler: ReturnType<typeof _.throttle> | null = null

  constructor(props: IUI2GeolocationFieldProps) {
    super(props)
    this.state = {
      status: 'locating',
      // Seed from the bound value so a reopened form can show a distance
      // before the browser produces a fresh fix.
      position: hasCoords(props.value) ? props.value : CACHED_LOCATION,
    }
  }

  public componentDidMount() {
    if (CACHED_LOCATION) this.applyPosition(CACHED_LOCATION)
    this.requestLocation()
    this.startWatching()
    this.requestLocationPermission()
  }

  public componentWillUnmount() {
    this.request?.cancel()
    if (this.permissionStatus) this.permissionStatus.onchange = null
    if (this.watchId !== null) {
      navigator.geolocation?.clearWatch(this.watchId)
      this.watchId = null
    }
    this.throttledWatchHandler?.cancel()
  }

  public render() {
    const { className, frames, transientValue, testID, accessibilityLabel } = this.props
    const banner = this.resolveBanner()
    return (
      <UI2FocusTargetRegistration
        frames={frames}
        transientValue={transientValue}
        hasError={this.hasError}
        borderRadius={BANNER_RADIUS}
        className={classNames('ui2-geolocationField', className)}
      >
        {banner && (
          <div data-testid={testID} aria-label={accessibilityLabel}>
            <UI2Banner
              mode={banner.mode}
              title={this.translate(banner.title)}
              text={this.translate(banner.text)}
              translationTable={this.props.translationTable}
              frames={frames}
            >
              {this.renderAction(banner)}
            </UI2Banner>
          </div>
        )}
      </UI2FocusTargetRegistration>
    )
  }

  /**
   * Web has no OS settings screen to open, so the recovery action is a link to
   * instructions for the browser the user is in — see `location-troubleshooting`.
   */
  private renderAction(banner: GeolocationBanner): React.ReactNode {
    if (banner.action === 'troubleshoot') {
      const href = resolveTroubleshootingUrl(
        this.props.troubleshootingUrl,
        typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      )
      return this.renderActionLink(this.translate(DEFAULT_TROUBLESHOOT_TEXT)!, href)
    }
    if (banner.action === 'maps') {
      const target = resolveDestinationGeolocation(this.props.destination)
      if (!target) return null
      const text = this.translate(this.props.openInMapsText ?? DEFAULT_OPEN_IN_MAPS_TEXT)!
      return this.renderActionLink(text, buildMapsUrl(target))
    }
    return null
  }

  private renderActionLink(text: string, href: string): React.ReactNode {
    return (
      <a
        className='ui2-geolocationField-action'
        href={href}
        target='_blank'
        rel='noopener noreferrer'
      >
        <span>{text}</span>
        <UI2Icon name={UI2IconNames.EXTERNAL_LINK} size={14} />
      </a>
    )
  }

  private resolveBanner(): GeolocationBanner | undefined {
    const { destination, distanceUnit } = this.props
    const distanceText = resolveDistanceText(this.state.position, destination, distanceUnit)
    return resolveGeolocationBanner(this.state.status, this.props, distanceText)
  }

  private translate(text: string | undefined): string | undefined {
    if (!text) return undefined
    return translateString(text, this.props.translationTable) ?? text
  }

  private hasError = (): boolean => {
    return !!this.props.isRequired && !hasCoords(this.props.value)
  }

  private applyPosition = (coords: GeolocationCoords) => {
    if (!hasCoords(coords)) return
    if (!_.isEqual(this.props.value, coords)) {
      this.props.onChange?.(coords)
    }
    this.setState({ status: 'found', position: coords })
  }

  private requestLocation = () => {
    this.request = getCurrentGeoPosition().then(
      (position: GeolocationPosition) => {
        const { longitude, latitude } = position.coords
        CACHED_LOCATION = { longitude, latitude }
        this.applyPosition(CACHED_LOCATION)
      },
      (gpsError: GeolocationError) => {
        this.setState({ status: resolveErrorStatus(gpsError) })
      },
    )
  }

  private requestLocationPermission() {
    void navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((permissionStatus: PermissionStatus) => {
        this.permissionStatus = permissionStatus
        permissionStatus.onchange = () => {
          if (permissionStatus.state !== 'granted') return
          this.setState({ status: 'locating' })
          this.requestLocation()
          this.startWatching()
        }
      })
  }

  private startWatching = () => {
    if (!navigator.geolocation) return
    const { minSecondsBetweenUpdates = 5, watchPosition } = this.props
    if (!watchPosition) return

    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId)
      this.throttledWatchHandler?.cancel()
      this.watchId = null
    }

    this.throttledWatchHandler = _.throttle((position: GeolocationPosition) => {
      const { longitude, latitude } = position.coords
      if (CACHED_LOCATION?.latitude === latitude && CACHED_LOCATION?.longitude === longitude) return
      CACHED_LOCATION = { longitude, latitude }
      this.applyPosition(CACHED_LOCATION)
    }, minSecondsBetweenUpdates * 1000)

    this.watchId = navigator.geolocation.watchPosition(
      this.throttledWatchHandler,
      (nativeError: GeolocationPositionError) => {
        this.setState({ status: resolveErrorStatus({ nativeError }) })
      },
      { enableHighAccuracy: true, timeout: 10000 },
    )
  }
}

const BANNER_RADIUS = 6

/**
 * Only a denied permission is recoverable by the user, so a timeout or an
 * unfixable position lands on `unavailable` and gets no action.
 */
function resolveErrorStatus(gpsError: GeolocationError | undefined): GeolocationStatus {
  return gpsError?.nativeError?.code === GeolocationPositionError.PERMISSION_DENIED
    ? 'denied'
    : 'unavailable'
}
